import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { ColDef } from "ag-grid-community";
import { cloneDeep, isEqual } from "lodash";
import { filter, firstValueFrom, take } from "rxjs";
import { CorpPayor } from "src/app/common/interfaces/corp-payor.interface";
import { ChangeTracker } from "../../../../common/classes/change-tracker.class";
import { ConfirmationModalComponent } from "../../../../common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { HierarchyComponent } from "../../../../common/components/hierarchy/hierarchy.component";
import { Hierarchy } from "../../../../common/components/hierarchy/hierarchy.interface";
import { ClientSideListBaseComponent } from "../../../../common/core/client-side-list-base/client-side-list-base.component";
import { EntityManagementLayoutComponent } from "../../../../common/layouts/entity-management-layout.component";
import { CorpPayorClient } from "../../../../common/services/corp-payor.client";
import { PayorStore } from "../payor-store.service";
import { payorAssignedFacilitiesColumns } from "./payor-assigned-facilities.component";
import { payorUnassignedFacilities } from "./payor-facilities.config";
import { ChangesSaved } from "src/app/common/constants/core-constants";
import { ActivatedRoute, Router } from "@angular/router";
import { SecurityFeaturePermission } from "src/app/features/security-group/interfaces/security-feature-permission.interface";
import { SecurityFeatureUtility } from "src/app/common/services/security-feature.utility";
import { Features } from "src/app/common/enums/features.enum";

@Component({
  selector: "app-payor-facilities",
  templateUrl: "payor-facilities.component.html",
  imports: [
    CommonModule,
    HierarchyComponent,
    ClientSideListBaseComponent,
    MatDialogModule,
    EntityManagementLayoutComponent,
  ],
  standalone: true,
})
export class PayorFacilitiesComponent extends ChangeTracker implements OnInit {
  payor!: CorpPayor | null;
  loading = true;
  hierarchyData!: Hierarchy | null;
  columnDefs!: ColDef[];
  hierarchyColumns!: ColDef[];
  facilitiesSelected: any[] = [];
  gridContext: any;
  rowData: any[] = []; // change this type when we can wire the data up
  corporationId: string = '';
  payorPlanPermissions: SecurityFeaturePermission = { canView: false, canManage: false };

  constructor(
    private payorStore: PayorStore,
    private dialog: MatDialog,
    private corpPayorClient: CorpPayorClient,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private securityFeatureUtility: SecurityFeatureUtility
  ) {
    super();
  }
  async ngOnInit(): Promise<void> {
    this.fetchPermissions();

    const params = await firstValueFrom(this.activatedRoute.queryParams)
    this.corporationId = params['corporationId']
    this.payorStore.payor$
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe(async (payor) => {
        this.payor = payor;
        this.getHierarchyData();

        this.gridContext = {
          idKey: "facilityid",
          actions: [],
        };
        this.setupColumns();
        this.loading = false;
      });
  }

  setupColumns(): void {
    this.hierarchyColumns = payorUnassignedFacilities;
    this.columnDefs = payorAssignedFacilitiesColumns;
  }

  async onSave(showModal: boolean = true): Promise<void> {
    try {
      await firstValueFrom(
        this.corpPayorClient.editPayorFacilities(
          this.facilitiesSelected.map((x) => ({
            id: x.id,
            facilityid: x.facilityid,
            payorid: this.payor!.id,
            status: x.checked || false,
          }))
        )
      );
      this.getHierarchyData();
      this.formGroup.markAsPristine();
      if (showModal) {
        this.dialog.open(ConfirmationModalComponent, {
          data: ChangesSaved,
        });
      }
    } catch (error: any) {
      this.dialog.open(ConfirmationModalComponent, {
        data: {
          title: "Error",
          message: error?.error,
          buttonText: "OK",
          hideCancelButton: true,
          error: true,
        },
      });
    }
  }

  onCancelPayorFacilities(){
    this.router.navigate([
      '../../corporation', 
      this.corporationId, 
      'settings'
    ], {
      queryParams: { view: 'payor' }
    });
  }

  async onFacilitiesSelected(facilities: any[]): Promise<void> {
    // on initial load, this will be called with full facility list
    // and don't want to mark as dirty if nothing has changed
    const initialSelected = (this.facilitiesSelected || [])
      .filter(facility => facility.checked)
      .map((x) => x.id);
    const newSelected = (facilities || [])
      .filter(facility => facility.checked)
      .map((x) => x.id);
    if (!isEqual(initialSelected.sort(), newSelected.sort())) {
      this.formGroup.markAsDirty();
    } else {
      this.formGroup.markAsPristine();
    }
    // clone deep, otherwise this internal tracking array and the output from hiearchy
    // are the same reference and will never mark as dirty
    this.facilitiesSelected = cloneDeep(facilities);
  }

  private async getHierarchyData() {
    const payorFacilityData = await firstValueFrom(
      this.corpPayorClient.getPayorFacilities(
        this.payor?.id as string,
        this.payor?.corpid as string
      )
    );

    this.rowData = payorFacilityData.payorfacilitiesassigned;

    this.facilitiesSelected = payorFacilityData.payorfacilitiesassigned.map(
      (x) => ({
        id: x.id,
        checked: true,
      })
    );
    this.hierarchyData = {
      hierarchies: payorFacilityData.facilityhierarchyitems as any,
    } as any;

    this.hierarchyData?.hierarchies.forEach((each) => {
      this.setDefaultChecked(each);
    });

    this.hierarchyData?.hierarchies.forEach((each) => {
      this.checkParent(each);
    });
  }

  checkParent(searchPivot: any) {
    if (searchPivot.levels?.length) {
      searchPivot.levels.forEach((facility: any) => this.checkParent(facility));
    }

    if (searchPivot.items?.length) {
      const allChecked = searchPivot.items.every((x: any) => x.checked);
      if (allChecked) {
        searchPivot.checked = true;
      }
    }

    if (searchPivot.levels?.length) {
      const allChecked = searchPivot.levels.every((x: any) => x.checked);
      if (allChecked) {
        searchPivot.checked = true;
      }
    }
  }

  setDefaultChecked(searchPivot: any) {
    if (searchPivot.facilityname) {
      const facility = this.rowData.find((x) => x.id === searchPivot.id);
      if (facility) {
        searchPivot.checked = true;
        searchPivot.addedToPayor = true;
      }
    }

    if (searchPivot.items) {
      searchPivot.items.forEach((facility: any) =>
        this.setDefaultChecked(facility)
      );
    }

    if (searchPivot.levels) {
      searchPivot.levels.forEach((facility: any) =>
        this.setDefaultChecked(facility)
      );
    }
  }

  override saveAndContinue(): void {
    this.onSave(false);
  }

  private fetchPermissions() {
    this.payorPlanPermissions = this.securityFeatureUtility.getPermissions(Features.CORP_SETTINGS_PAYOR_PLAN);
  }
}
